import { useCallback } from "react";

/**
 * Custom hook for handling form element reordering via drag and drop
 * Provides utilities for reordering elements within and between subsections
 */
export const useFormReorder = ({ formData, setFormData, setSelectedElement, customWorkflowVariables, formTabs  }) => {
  /**
   * Check if an element is visible (passes the filter)
   * Matches FormCanvas filtering logic
   */
  const isElementVisible = useCallback(
    (element) => {
      // Filter out hidden elements
      if (element.properties?.isHidden) {
        return false;
      }
      // Filter out workflow variables that don't exist in customWorkflowVariables
      if (element.properties?.isWorkflowVariable) {
        const usedVar = customWorkflowVariables?.find((v) => v.id === element.properties.workflowVariableId);
        return usedVar ? true : false;
      }
      return true;
    },
    [customWorkflowVariables]
  );

  /**
   * Filter elements to match FormCanvas filtering logic
   * Filters out hidden elements and workflow variables that don't exist in customWorkflowVariables
   */
  const filterVisibleElements = useCallback(
    (elements) => {
      return elements.filter(isElementVisible);
    },
    [isElementVisible]
  );

  /**
   * Reorders elements within the same subsection
   * Handles hidden elements by working with visible elements only
   */
  const reorderWithinSubsection = useCallback(
    (subsectionId, sourceIndex, destinationIndex, formTabs) => {
      setFormData((prev) => ({
        ...prev,
        [formTabs.hostSection]: prev[formTabs.hostSection].map((sub) => {
          if (sub.id === subsectionId) {
            // Get only visible elements (matching the filter in FormCanvas)
            const visibleElements = filterVisibleElements(sub.elements);

            // Reorder visible elements
            const reorderedVisible = [...visibleElements];
            const [movedElement] = reorderedVisible.splice(sourceIndex, 1);
            reorderedVisible.splice(destinationIndex, 0, movedElement);

            // Replace visible elements in the original array while keeping filtered-out elements
            const newElements = [...sub.elements];
            let visibleIndex = 0;

            for (let i = 0; i < newElements.length; i++) {
              if (isElementVisible(newElements[i])) {
                newElements[i] = reorderedVisible[visibleIndex];
                visibleIndex++;
              }
            }

            return { ...sub, elements: newElements };
          }
          return sub;
        }),
      }));
    },
    [setFormData, filterVisibleElements, isElementVisible]
  );

  /**
   * Moves element from one subsection to another
   * Handles hidden elements by working with visible elements only
   */
  const moveBetweenSubsections = useCallback(
    (sourceSubsectionId, destinationSubsectionId, sourceIndex, destinationIndex, formTabs) => {
      setFormData((prev) => {
        let movedElement = null;

        // First pass: remove element from source subsection
        const updatedSubsections = prev[formTabs.hostSection].map((sub) => {
          if (sub.id === sourceSubsectionId) {
            // Get only visible elements (matching the filter in FormCanvas)
            const visibleElements = filterVisibleElements(sub.elements);

            // Remove from visible elements
            const updatedVisible = [...visibleElements];
            [movedElement] = updatedVisible.splice(sourceIndex, 1);

            // Reconstruct array: keep filtered-out elements, replace visible ones
            const newElements = [];
            let visibleIndex = 0;

            for (let i = 0; i < sub.elements.length; i++) {
              const element = sub.elements[i];
              if (!isElementVisible(element)) {
                // Keep filtered-out elements (hidden or invalid workflow variables)
                newElements.push(element);
              } else {
                // Replace with updated visible elements (skip the removed one)
                if (visibleIndex < updatedVisible.length) {
                  newElements.push(updatedVisible[visibleIndex]);
                }
                visibleIndex++;
              }
            }

            return { ...sub, elements: newElements };
          }
          return sub;
        });

        // Second pass: add element to destination subsection
        const finalSubsections = updatedSubsections.map((sub) => {
          if (sub.id === destinationSubsectionId && movedElement) {
            // Get only visible elements (matching the filter in FormCanvas)
            const visibleElements = filterVisibleElements(sub.elements);

            // Insert at destination in visible elements
            const updatedVisible = [...visibleElements];
            updatedVisible.splice(destinationIndex, 0, movedElement);

            // Replace visible elements in original array
            const newElements = [...sub.elements];
            let visibleIndex = 0;

            for (let i = 0; i < newElements.length; i++) {
              if (isElementVisible(newElements[i])) {
                newElements[i] = updatedVisible[visibleIndex];
                visibleIndex++;
              }
            }

            // If we have more visible elements than original (moved element added), append them
            while (visibleIndex < updatedVisible.length) {
              newElements.push(updatedVisible[visibleIndex]);
              visibleIndex++;
            }

            return { ...sub, elements: newElements };
          }
          return sub;
        });

        return { ...prev, [formTabs.hostSection]: finalSubsections };
      });
    },
    [setFormData, filterVisibleElements, isElementVisible]
  );

  /**
   * Main drag end handler for react-beautiful-dnd
   */
  const handleDragEnd = useCallback(
    (result) => {
      const { destination, source, draggableId } = result;      // Do nothing if dropped outside a droppable area
      if (!destination) {
        return;
      }

      // Do nothing if dropped in the same position
      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return;
      }

      // Clear selected element during reorder to prevent stale selection
      setSelectedElement(null);

      // Handle reordering within the same subsection
      if (source.droppableId === destination.droppableId) {
        reorderWithinSubsection(source.droppableId, source.index, destination.index, formTabs);
      } else {
        // Handle moving between different subsections
        moveBetweenSubsections(source.droppableId, destination.droppableId, source.index, destination.index, formTabs);
      }
    },
    [reorderWithinSubsection, moveBetweenSubsections, setSelectedElement, formTabs]
  );

  /**
   * Check if an element can be dragged (excludes certain types if needed)
   */
  const isDraggable = useCallback((element) => {
    // Add any restrictions here if needed
    // For example: return element.type !== 'some-restricted-type';
    return true;
  }, []);

  return {
    handleDragEnd,
    isDraggable,
  };
};
